/**
 * UpgradeDialog Component
 *
 * A flexible upgrade dialog that handles multiple contexts:
 * - storage_limit: Storage limit reached
 * - table_limit: Maximum tables reached
 * - team_limit: Maximum team members reached
 * - subscription_expired: Subscription not renewed/expired
 */

import { useState } from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { AlertCircle, Database, Users, CreditCard, X, Sparkles, Settings } from 'lucide-react';
import { Link } from '@tanstack/react-router';
import type { BillingInterval } from '@loader/shared-types';
import {
  Dialog,
  DialogDescription,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
} from '@loader/ui';
import { Progress } from '@loader/ui';

import { PlanCard } from './PlanCard';

import { usePlans, useCheckoutSession, useSubscription } from '@/hooks';
import { formatBytes } from '@/lib/formatters';

export type UpgradeDialogContext =
  | 'storage_limit'
  | 'table_limit'
  | 'team_limit'
  | 'subscription_expired'
  | 'upgrade';

interface BaseDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onUpgrade?: (planId: string, billingInterval: BillingInterval) => void;
  /** Workspace ID - when provided, the dialog handles checkout internally if no onUpgrade is given */
  workspaceId?: string;
  /** Current plan ID to highlight and disable selection */
  currentPlanId?: string;
  /** Whether the user is on a trial */
  isTrial?: boolean;
  /** ID of the plan currently being processed (shows loading state) */
  loadingPlanId?: string | null;
}

interface StorageLimitProps extends BaseDialogProps {
  context: 'storage_limit';
  storageUsage: number; // bytes
  storageLimit: number; // bytes
}

interface TableLimitProps extends BaseDialogProps {
  context: 'table_limit';
  tableCount: number;
  tableLimit: number;
}

interface TeamLimitProps extends BaseDialogProps {
  context: 'team_limit';
  memberCount: number;
  memberLimit: number;
}

interface SubscriptionExpiredProps extends BaseDialogProps {
  context: 'subscription_expired';
}

interface UpgradeProps extends BaseDialogProps {
  context: 'upgrade';
}

export type UpgradeDialogProps =
  | StorageLimitProps
  | TableLimitProps
  | TeamLimitProps
  | SubscriptionExpiredProps
  | UpgradeProps;

/**
 * Get dialog configuration based on context
 */
function getDialogConfig(props: UpgradeDialogProps) {
  switch (props.context) {
    case 'storage_limit':
      return {
        icon: AlertCircle,
        iconClassName: 'text-destructive',
        title: 'Storage Limit Reached',
        description: 'Upgrade your plan to continue versioning files.',
      };
    case 'table_limit':
      return {
        icon: Database,
        iconClassName: 'text-destructive',
        title: 'Table Limit Reached',
        description: 'Upgrade your plan to create more tables.',
      };
    case 'team_limit':
      return {
        icon: Users,
        iconClassName: 'text-destructive',
        title: 'Team Member Limit Reached',
        description: 'Upgrade your plan to invite more team members.',
      };
    case 'subscription_expired':
      return {
        icon: CreditCard,
        iconClassName: 'text-amber-500',
        title: 'Subscription Expired',
        description: 'Renew your subscription to continue using all features.',
      };
    case 'upgrade':
      return {
        icon: Sparkles,
        iconClassName: 'text-amber-500',
        title: 'Upgrade Your Plan',
        description: 'Get more storage, tables, and team members with a higher plan.',
      };
  }
}

/**
 * Usage display component for limit-based contexts
 */
function UsageDisplay({
  label,
  current,
  limit,
  formatValue,
}: {
  label: string;
  current: number;
  limit: number;
  formatValue?: (value: number) => string;
}) {
  const percentUsed = (current / limit) * 100;
  const format = formatValue || ((v: number) => v.toString());

  return (
    <div className="bg-muted/30 space-y-2 rounded-lg border p-4">
      <div className="flex items-center justify-between text-sm">
        <span className="text-muted-foreground">{label}</span>
        <span className="font-medium">
          {format(current)} / {format(limit)}
        </span>
      </div>
      <Progress value={Math.min(percentUsed, 100)} className="h-2" />
      <p className="text-muted-foreground text-xs">
        {percentUsed.toFixed(1)}% used
      </p>
    </div>
  );
}

export function UpgradeDialog(props: UpgradeDialogProps) {
  const { open, onOpenChange, onUpgrade, workspaceId, isTrial: isTrialProp, loadingPlanId: externalLoadingPlanId } = props;
  const config = getDialogConfig(props);
  const Icon = config.icon;

  const billingInterval: BillingInterval = 'monthly';

  // Internal checkout state (used when no onUpgrade callback is provided)
  const [internalLoadingPlanId, setInternalLoadingPlanId] = useState<string | null>(null);

  // Get subscription info for internal checkout (only when workspaceId is provided)
  const { plan: subscriptionPlan, isTrial: isTrialFromSub, hasStripeSubscription } = useSubscription(workspaceId || '');

  // Use internal checkout when no external onUpgrade is provided
  const { createCheckout, changePlan, isPending } = useCheckoutSession(workspaceId || '', {
    onSuccess: () => {
      setInternalLoadingPlanId(null);
    },
    onError: () => {
      setInternalLoadingPlanId(null);
    },
  });

  const useInternalCheckout = !onUpgrade && !!workspaceId;
  const currentPlanId = props.currentPlanId ?? (useInternalCheckout ? subscriptionPlan?.id : undefined);
  const isTrial = isTrialProp ?? (useInternalCheckout ? isTrialFromSub : false);
  const loadingPlanId = externalLoadingPlanId ?? (useInternalCheckout ? internalLoadingPlanId : null);

  // Fetch plans - only when dialog is open
  const { plans, isLoading: plansLoading } = usePlans({ enabled: open });

  // Subscription expired dialog should not be dismissable
  const isDismissable = props.context !== 'subscription_expired';

  const handleUpgrade = (planId: string) => {
    if (onUpgrade) {
      onUpgrade(planId, billingInterval);
    } else if (workspaceId) {
      // Internal checkout handling
      setInternalLoadingPlanId(planId);
      if (hasStripeSubscription) {
        changePlan({ planId, billingInterval });
      } else {
        createCheckout({ planId, billingInterval });
      }
    }
  };

  // Show only overlay while plans are loading
  const showContent = !plansLoading && plans && plans.length > 0;

  // Get current plan price for upgrade/downgrade text
  const currentPlan = plans?.find((p) => p.id === currentPlanId);
  const currentPlanPrice = currentPlan
    ? (billingInterval === 'monthly' ? currentPlan.monthly_price_cents : currentPlan.yearly_price_cents)
    : null;

  return (
    <Dialog open={open} onOpenChange={isDismissable ? onOpenChange : undefined}>
      <DialogPortal>
        <DialogOverlay className="bg-black/70 backdrop-blur-sm duration-300 ease-out" />
        {showContent && (
          <DialogPrimitive.Content
            className="bg-background data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-top-[1%] data-[state=open]:slide-in-from-top-[1%] fixed left-[50%] top-[50%] z-50 grid w-fit translate-x-[-50%] translate-y-[-50%] gap-4 rounded-lg border p-6 shadow-lg duration-300 ease-out"
            onPointerDownOutside={isDismissable ? undefined : (e) => e.preventDefault()}
            onEscapeKeyDown={isDismissable ? undefined : (e) => e.preventDefault()}
          >
            {isDismissable && (
              <DialogPrimitive.Close className="ring-offset-background focus:ring-ring data-[state=open]:bg-accent data-[state=open]:text-muted-foreground absolute right-4 top-4 rounded-xs opacity-70 transition-opacity hover:opacity-100 focus:outline-hidden focus:ring-2 focus:ring-offset-2 disabled:pointer-events-none">
                <X className="h-4 w-4" />
                <span className="sr-only">Close</span>
              </DialogPrimitive.Close>
            )}

            <div className="inline-flex flex-col gap-4">
              <DialogHeader>
                <div className="flex items-center gap-2">
                  <Icon className={`h-5 w-5 ${config.iconClassName}`} />
                  <DialogTitle>{config.title}</DialogTitle>
                </div>
                <DialogDescription>{config.description}</DialogDescription>
              </DialogHeader>

              {props.context === 'storage_limit' && (
                <UsageDisplay
                  label="Current Usage"
                  current={props.storageUsage}
                  limit={props.storageLimit}
                  formatValue={formatBytes}
                />
              )}

              {props.context === 'table_limit' && (
                <UsageDisplay
                  label="Tables"
                  current={props.tableCount}
                  limit={props.tableLimit}
                />
              )}

              {props.context === 'team_limit' && (
                <UsageDisplay
                  label="Team Members"
                  current={props.memberCount}
                  limit={props.memberLimit}
                />
              )}

              <div className="flex gap-4">
                {plans?.map((plan) => (
                  <PlanCard
                    key={plan.id}
                    plan={plan}
                    billingInterval={billingInterval}
                    isRecommended={plan.is_recommended}
                    loading={(isPending && internalLoadingPlanId === plan.id) || loadingPlanId === plan.id}
                    disabled={(isPending && internalLoadingPlanId !== plan.id) || (!!loadingPlanId && loadingPlanId !== plan.id)}
                    onUpgrade={handleUpgrade}
                    className="w-64"
                    showTrial={false}
                    isCurrentPlan={currentPlanId === plan.id}
                    isTrial={isTrial}
                    currentPlanPrice={currentPlanPrice}
                  />
                ))}
              </div>

              <div className="flex justify-center pt-2">
                <Link
                  to="/settings/billing"
                  onClick={() => onOpenChange(false)}
                  className="text-muted-foreground hover:text-foreground inline-flex items-center gap-1.5 text-sm transition-colors"
                >
                  <Settings className="h-3.5 w-3.5" />
                  Manage billing settings
                </Link>
              </div>
            </div>
          </DialogPrimitive.Content>
        )}
      </DialogPortal>
    </Dialog>
  );
}
